<template>
  <div class="orderFormBid">
    <div class="form__card">
      <div class="form__block">
        <h4 class="form__title">{{ $t('bid.form.customerTitle') }}</h4>
        <div v-if="isExternalHotelSource" class="pt-24 pb-8">
          <i class="owl-status-triangle-warning text-orange" />
          <span class="font-semibold">{{ $t('enNameInputWarning') }}</span>
        </div>
        <div class="form__form w-full xl:w-2/3">
          <div class="form__form-full flex flex-wrap">
            <div class="form__form-half">
              <label for="customerFirstName">
                <template v-if="isExternalHotelSource">{{ $t('enFirstName') }}</template>
                <template v-else>{{ $t('firstName') }}</template>
              </label>
              <input
                type="text"
                v-model="customerFirstName"
                class="form__content input w-full"
                name="customerFirstName"
                :placeholder="isExternalHotelSource ? $t('enFirstName') : $t('firstName')"
              >
              <div class="errorMsg">{{ validation.firstError('customerFirstName') }}</div>
            </div>
            <div class="form__form-half mt-32 sm:mt-0">
              <label for="customerLastName">
                <template v-if="isExternalHotelSource">{{ $t('enLastName') }}</template>
                <template v-else>{{ $t('lastName') }}</template>
              </label>
              <input
                type="text"
                v-model="customerLastName"
                class="form__content input w-full"
                name="customerLastName"
                :placeholder="isExternalHotelSource ? $t('enLastName') : $t('lastName')"
              >
              <div class="errorMsg">{{ validation.firstError('customerLastName') }}</div>
            </div>
          </div>
          <div class="form__form-full flex flex-wrap">
            <div class="w-full sm:w-1/3 sm:pr-8 pl-0 flex-no-shrink">
              <label for="">{{ $t('phoneCode') }}</label>
              <el-select
                v-model="customerPhoneCode"
                class="form__content phone-code-select w-full"
                filterable
                :no-match-text="$t('noResults')"
                :no-data-text="$t('number.0')"
                :placeholder="$t('phoneCode')">
                <el-option
                  v-for="(country, index) in this['user/phoneCodes']"
                  :key="`customer-phonecode-${ index }-${ country.phoneCode }`"
                  :label="`+${ country.phoneCode } (${ country.name })`"
                  :value="country.phoneCode">
                </el-option>
              </el-select>
              <div class="errorMsg">{{ validation.firstError('customerPhoneCode') }}</div>
            </div>
            <div class="sm:pl-8 w-full sm:w-2/3 mt-32 sm:mt-0">
              <label for="">{{ $t('phone') }}</label>
              <input type="tel" v-model="customerPhoneNumber" class="form__content input w-full" name="customerPhoneNumber" :placeholder="$t('phone')">
              <div class="errorMsg">{{ validation.firstError('customerPhoneNumber') }}</div>
            </div>
          </div>
          <div class="form__form-full">
            <label for="">{{ $t('userTitle') }}</label>
            <div class="form__gender flex mt-8 flex-col sm:flex-row">
              <div class="form__gender-radio owl-radio-wrap">
                <label for="customerGenderMale">
                  <input type="radio" id="customerGenderMale" name="customerGender" v-model="customerGender" value="male">
                  <div class="owl-radio"></div>
                  <span>{{ $t('male') }}</span>
                </label>
              </div>
              <div class="form__gender-radio owl-radio-wrap mt-16 sm:mt-0">
                <label for="customerGenderFemale">
                  <input type="radio" id="customerGenderFemale" name="customerGender" v-model="customerGender" value="female">
                  <div class="owl-radio"></div>
                  <span>{{ $t('female') }}</span>
                </label>
              </div>
              <div class="form__gender-radio owl-radio-wrap mt-16 sm:mt-0">
                <label for="customerGenderUnknown">
                  <input type="radio" id="customerGenderUnknown" name="customerGender" v-model="customerGender" value="unknown">
                  <div class="owl-radio"></div>
                  <span>{{ $t('notProvide') }}</span>
                </label>
              </div>
            </div>
          </div>
          <div class="form__form-full">
            <label for="customerEmail">{{ $t('email') }}</label>
            <input type="email" v-model="customerEmail" class="form__content input w-full" :class="{'hasEmail': customerEmail}" name="customerEmail" :placeholder="$t('email')" :disabled="this['user/isLogin'] ? customerEmail : false">
            <div class="errorMsg">{{ validation.firstError('customerEmail') }}</div>
          </div>
          <div v-if="!this['user/isLogin']" class="form__form-full">
            <label for="customerEmail">{{ $t('emailConfirm') }}</label>
            <input type="email" v-model="customerEmailConfirm" class="form__content input w-full" name="customerEmailConfirm" :placeholder="$t('email')" onpaste="return false;" >
            <div class="errorMsg">{{ validation.firstError('customerEmailConfirm') }}</div>
          </div>
        </div>
      </div>
    </div>
    <div class="form__card">
      <div class="form__block">
        <div class="text-lg font-bold mb-24">{{ $t('paymentWay.other') }}</div>
        <h4 class="form__title">{{ $t('specialRequest') }}</h4>
        <div class="form__form form-switched show">
          <div class="form__form-full">
            <div class="mb-8">{{ $t('note') }}</div>
            <textarea class="form__content input w-full resize-none" v-model="note" name="" id="" cols="30" rows="4" :placeholder="$t('pleaseEnterNote')" style="border-radius: 20px"></textarea>
          </div>
          <div class="form__block-notation" v-if="isExternalHotelSource">
            <i class="owl-status-triangle-warning text-orange text-2xl pr-4" />
            <span class="text-xs leading-normal">{{ $t('noteCountryWarningMsg', { country: this['order/order'].hotel.countryText }) }}</span>
          </div>
          <div class="form__block-notation bg-grey-lightest font-medium mt-16 p-8 text-xs text-grey-darker flex items-start rounded-full" style="background: rgb(248, 250, 252);">
            <i class="owl-status-triangle-warning text-orange text-2xl pr-4" />
            <span class="text-xs leading-normal">{{ $t('noteWarningMsg') }}</span>
          </div>
        </div>
      </div>
      <div class="p-16 sm:p-24">
        <div class="border-t border-grey-lightest pt-16">
          <div class="form__block-head items-center">
            <h4 class="form__title">{{ $t('needReceipt') }} ({{ $t('notRequired') }})</h4>
            <div class="form__switcher">
              <label class="owl-switch__wrap">
                <input type="checkbox" v-model="needReceipt">
                <div class="owl-switch__item">
                  <div class="owl-switch__bg">
                    <div class="owl-switch__button"></div>
                  </div>
                </div>
              </label>
            </div>
          </div>
          <div class="form__form form-switched" :class="needReceipt ? 'show' : ''">
            <div class="form__form w-full xl:w-2/3">
              <div class="form__form-full flex flex-wrap">
                <div class="form__form-half">
                  <label for="companyName">{{ $t('companyName') }}</label>
                  <input type="text" v-model="companyName" class="form__content input w-full" name="companyName" :placeholder="$t('companyName')">
                  <div class="errorMsg">{{ validation.firstError('companyName') }}</div>
                </div>
                <div class="form__form-half mt-32 sm:mt-0">
                  <label for="companyNumber">{{ $t('companyNumber') }}</label>
                  <input type="text" v-model="companyNumber" class="form__content input w-full" name="companyNumber" :placeholder="$t('companyNumber')">
                  <div class="errorMsg">{{ validation.firstError('companyNumber') }}</div>
                </div>
              </div>
            </div>
          </div>
          <div class="form__block-notation bg-grey-lightest font-medium mt-16 p-8 text-xs text-grey-darker flex items-start rounded-full" style="background: rgb(248, 250, 252);">
            <i class="owl-status-triangle-warning text-orange text-2xl pr-4" />
            <span class="text-xs leading-normal">{{ this['order/order'].hotel.receipt.context }}</span>
          </div>
        </div>
      </div>
    </div>
    <template v-if="this['order/order'].allowances">
      <div v-for="allowance in this['order/order'].allowances" class="form__card p-16 form__card--blue" :key="allowance.key">
        <div class="form__switcher-checkbox owl-checkbox-wrap">
          <label :for="allowance.key">
            <input type="checkbox" :id="allowance.key" :checked="isAllowance(allowance.key)" @change="toggleAllowance(allowance.key)">
            <div class="owl-checkbox"></div>
            <span>{{ $t('allowanceCheck', { allowanceName: allowance.name }) }}</span>
          </label>
          <div class="flex items-center mt-16 -ml-4">
            <i class="owl-status-triangle-warning text-blue-lighter text-2xl mt-4"></i>
            <span class="text-sm text-blue font-bold">{{ $t('allowanceCheckWarning') }}</span>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import _ from 'lodash'
import SimpleVueValidator from 'simple-vue-validator'
import fullPageModal from '@/components/modals/fullPageModal'
import contractContent from '@/components/contractContent'
import { createHelpers } from 'vuex-map-fields'
import { mapGetters, mapMutations } from 'vuex'
import animateScrollTo from 'animated-scroll-to'

const { mapFields } = createHelpers({
  getterType: 'order/getField',
  mutationType: 'order/updateField'
})

const Validator = SimpleVueValidator.Validator

export default {
  name: 'OrderFormBid',
  metaInfo () {
    return {
      title: '填寫個人資料'
    }
  },
  data () {
    return {}
  },
  validators: {
    'customerFirstName': function (val) {
      const rule = Validator.value(val).required()

      if (this.isExternalHotelSource && !/^[a-zA-Z\s]*$/.test(val)) {
        // 如為外部房源，旅客姓名需輸入英文
        return rule.custom(() => {
          return this.$t('validatorErrors.regex')
        })
      }
      return rule
    },
    'customerLastName': function (val) {
      const rule = Validator.value(val).required()

      if (this.isExternalHotelSource && !/^[a-zA-Z\s]*$/.test(val)) {
        return rule.custom(() => {
          return this.$t('validatorErrors.regex')
        })
      }
      return rule
    },
    'customerPhoneCode': function (val) {
      return Validator.value(val).required()
    },
    'customerPhoneNumber': function (val) {
      return Validator.value(val).required().minLength(7)
    },
    'customerEmail': function (val) {
      return Validator.value(val).required().email()
    },
    'customerEmailConfirm, customerEmail': function (confirmEmail, email) {
      return this['user/isLogin']
        ? null
        : Validator.value(confirmEmail).required().match(email)
    },
    'companyName': function (val) {
      const rule = Validator.value(val).minLength(2)
      if (this.needReceipt) {
        if (!this.companyNumber) {
          if (val) { this.$validate('companyNumber') }
        }
        return rule.required()
      }
    },
    'companyNumber': function (val) {
      const rule = Validator.value(val)
        .digit()
        .length(8)
        .custom(() => {
          if (val.length === 8) {
            const idMultiplyRule = [1, 2, 1, 2, 1, 2, 4, 1]
            let isSpecial = false
            const total = String(val).split('').reduce((accumulator, currentValue, index, array) => {
              let sum = Number(currentValue) * idMultiplyRule[index]
              if (index === 6 && Number(currentValue) === 7) {
                isSpecial = true
              }
              if (sum > 9) {
                sum = Number(String(sum).split('')[0]) + Number(String(sum).split('')[1])
              }
              return Number(accumulator) + sum
            })
            if (isSpecial ? (total % 5 !== 0 && (total + 1) % 5 !== 0) : total % 5 !== 0) {
              return this.$t('validatorErrors.companyNumberIsrIncorrect')
            }
          }
        })
      if (this.needReceipt) {
        if (!this.companyName) {
          if (val) { this.$validate('companyName') }
        }
        return rule.required()
      }
    }
  },
  computed: {
    ...mapGetters([
      'hotels/externalHotelSource',
      'user/profile',
      'user/currencySign',
      'user/phoneCodes',
      'search/totalNights',
      'order/order',
      'order/isQuarantineOrder',
      'user/isLogin'
    ]),
    ...mapFields({
      asOrder: 'form.asOrder',
      needSmoke: 'form.needSmoke',
      specialRequest: 'form.specialRequest',
      needReceipt: 'form.needReceipt',
      companyName: 'form.receiptInfo.companyName',
      companyNumber: 'form.receiptInfo.companyNumber',
      infantBed: 'form.infantBed',
      checkInEarly: 'form.checkInEarly',
      checkOutLately: 'form.checkOutLately',
      lowFloor: 'form.lowFloor',
      noSideRoom: 'form.noSideRoom',
      innerWindow: 'form.innerWindow',
      bedRequest: 'form.bedRequest',
      note: 'form.note',
      customerFirstName: 'form.customer.firstName',
      customerLastName: 'form.customer.lastName',
      customerPhoneCode: 'form.customer.phoneCode',
      customerPhoneNumber: 'form.customer.phoneNumber',
      customerGender: 'form.customer.gender',
      customerEmail: 'form.customer.email',
      customerEmailConfirm: 'form.customer.emailConfirm',
      travelerFirstName: 'form.traveler.firstName',
      travelerLastName: 'form.traveler.lastName',
      travelerPhoneCode: 'form.traveler.phoneCode',
      travelerPhoneNumber: 'form.traveler.phoneNumber',
      travelerGender: 'form.traveler.gender',
      travelerEmail: 'form.traveler.email',
      sendToTraveler: 'form.sendToTraveler',
      allowances: 'form.allowances'
    }),
    currencySign: function () {
      return this['user/currencySign']
    },
    allowEarlyCheckInPricePerHour: function () {
      if (Number(this['order/order'].hotel.check.allowEarlyCheckInPricePerHour) > 0) {
        return this['order/order'].hotel.check.allowEarlyCheckInPricePerHour
      } else {
        // return this.$t('free')
        return 0
      }
    },
    allowLateCheckOutPricePerHour: function () {
      if (Number(this['order/order'].hotel.check.allowLateCheckOutPricePerHour) > 0) {
        return this['order/order'].hotel.check.allowLateCheckOutPricePerHour
      } else {
        // return this.$t('free')
        return 0
      }
    },
    hasOrder: function () {
      return !_.isEmpty(this['order/order'])
    },
    isExternalHotelSource: function () {
      return this['hotels/externalHotelSource'].includes(this['order/order'].source)
    }
  },
  methods: {
    ...mapMutations([
      'order/ADD_ALLOWANCE',
      'order/REMOVE_ALLOWANCE'
    ]),
    toggleAllowance: function (allowanceKey) {
      console.debug('allowanceKey', allowanceKey)
      if (this.isAllowance(allowanceKey)) {
        this['order/REMOVE_ALLOWANCE'](allowanceKey)
      } else {
        this['order/ADD_ALLOWANCE'](allowanceKey)
      }
    },
    isAllowance: function (allowanceKey) {
      return this.allowances.includes(allowanceKey)
    },
    syncTraveler: function () {
      if (this.asOrder) {
        this.travelerFirstName = this.customerFirstName
        this.travelerLastName = this.customerLastName
        this.travelerPhoneCode = this.customerPhoneCode
        this.travelerPhoneNumber = this.customerPhoneNumber
        this.travelerEmail = this.customerEmail
        this.sendToTraveler = false
      } else {
        this.travelerFirstName = ''
        this.travelerLastName = ''
        this.travelerPhoneCode = ''
        this.travelerPhoneNumber = ''
        this.travelerEmail = ''
      }
    },
    openContract: function () {
      this.$modal.show(fullPageModal, {
        // 輸入內容
        context: contractContent
      },
      {
        adaptive: true,
        name: 'contractDetail',
        // clickToClose: false,
        height: '90%',
        width: '90%',
        maxWidth: 1440,
        scrollable: true
      })
    },
    onFormValidate: function () {
      return new Promise((resolve, reject) => {
        try {
          this.$validate().then(success => {
            if (success) {
              resolve()
            } else {
              const errorForms = document.getElementsByClassName('errorMsg')
              let errorElement = {}
              for (const el of errorForms) {
                if (el.innerText !== '') {
                  errorElement = el
                  break
                }
              }

              if (!_.isEmpty(errorElement)) {
                animateScrollTo(errorElement, {
                  offset: -200,
                  speed: 800
                })
              }
              let errorString = {}
              for (const error of this.validation.errors) {
                errorString[error.field] = {
                  message: error.message
                }
              }
              reject(new Error('form error: ' + errorString.join(',')))
            }
          })
        } catch (e) {
          console.log('Error', e)
        }
      })
    },
    onContentExpand: function ($event) {
      const target = $event.target
      if (target.className.includes('expandable-content')) {
        target.classList.toggle('expanded')
      }
    }
  },
  mounted () {
    this.validation.reset()

    const user = this['user/profile']
    if (user) {
      this.customerFirstName = user.firstName
      this.customerLastName = user.lastName
      this.customerPhoneCode = !_.isEmpty(user.phoneCode) ? +user.phoneCode.replace('+', '') : ''
      this.customerPhoneNumber = user.phoneNumber
      this.customerEmail = user.email
      this.customerGender = user.gender
    }
  },
  watch: {
    customerFirstName: function () {
      this.syncTraveler()
    },
    customerLastName: function () {
      this.syncTraveler()
    },
    customerPhoneCode: function () {
      this.syncTraveler()
    },
    customerPhoneNumber: function () {
      this.syncTraveler()
    },
    customerEmail: function () {
      this.syncTraveler()
    },
    asOrder: {
      handler: function (val, oldVal) {
        this.syncTraveler()
      },
      immediate: true
    },
    specialRequest: {
      handler: function (val, oldVal) {
        if (val) {
          window.dataLayer.push({
            event: 'order_request_on'
          })
        } else {
          window.dataLayer.push({
            event: 'order_request_off'
          })
        }
      }
    }
  }
}
</script>

<style lang="sass" scoped>
.form__card
  @apply border border-grey-lighter
  @apply mb-24
  border-radius: 20px
  &--blue
    @apply border-blue-light bg-blue-lightest
  &--contract
    @extend .form__card
    @apply border-orange
    @apply bg-orange-lightest
.form__block
  @apply p-16
  @screen sm
    @apply p-24
  & + &
    @apply border-t-1 border-grey-lightest
.form__block-head
  @apply flex justify-between
.form__block-notation
  @apply mt-16
  @apply flex items-center
.form__form
  @apply mt-24
  &.form-switched
    @apply overflow-hidden
    @apply opacity-0
    @apply mt-0
    height: 0
    transition: opacity 0.3s ease
  &.show
    @apply opacity-100
    @apply h-full
    @apply mt-24

.form__form-half
  @apply w-full
  @screen sm
    @apply pr-8 pl-0 w-1/2
    & + &
      @apply pr-0 pl-8
.form__form-full
  @apply w-full
  & + &
    @apply mt-32

.form__title
  @apply font-bold
.form__section
  @apply text-sm text-grey-dark
  @apply mt-24
.form__content
  @apply text-sm leading-normal rounded-full
  @apply mt-8
.form__switcher
  @apply flex
.form__switcher-title
  @apply text-base text-grey-darkest
  & + .form__switcher
    @apply mt-8
.form__switcher-radio
  & + &
    @apply ml-32
// .form__switcher-checkbox
//   & + &
//     @apply ml-32
.errorMsg
  @apply text-xs text-red ml-4 mt-8

.form__gender
  &-radio
    @apply mr-32

.phone-code-select::v-deep
  .el-input__inner
    @apply border-none bg-grey-lightest text-grey-darker
    &::placeholder
      @apply text-grey-dark
    &:focus
      @apply bg-white border-grey-darkest border-1 border-grey-light
      border-style: solid

.expandable-content
  @apply overflow-hidden relative
  z-index: 1
  max-height: 100px
  @screen md
    max-height: 100%
  &::before
    @apply absolute w-full block pin-b pin-l
    height: 100px
    z-index: 2
    content: ''
    background: linear-gradient(0deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 35%)
    @screen md
      @apply hidden
  &.expanded
    max-height: 100%
    &::before
      @apply hidden
.hasEmail
  @apply opacity-75 #{!important}
</style>
<style lang="sass">
.orderFormBid
  .el-input
    input
      @apply rounded-full
</style>
